import type { CSSProperties } from "react";
import { LogoMark } from "./logo-mark";

/** The navigation's lockup, in the numbers the brand is built on: the mark is
 *  26px against a 15px wordmark, with an 8px gap, semibold, −0.02em. */
const NAV_WORD = 15;
const MARK_RATIO = 26 / NAV_WORD;
const GAP_RATIO = 8 / NAV_WORD;

/**
 * The brand lockup: the mark and the wordmark as one unit, at the proportions
 * the navigation uses.
 *
 * Anything that shows the brand outside the navigation — the hero explainer, a
 * deck, an email header — should use this rather than a mark placed next to some
 * text. Those two treatments look alike at 15px and stop looking alike the
 * moment either is resized: the mark's ratio to the wordmark, the gap and the
 * wordmark's weight and tracking all have to travel together or the lockup
 * drifts. `wordSize` is the only dial; everything else scales from it, and at
 * the default 15px this renders exactly what the navigation rendered before it
 * used this component.
 */
export function BrandLockup({
  wordSize = NAV_WORD,
  className = "",
  style,
}: {
  /** The wordmark's font size in px; the mark and gap scale from it. */
  wordSize?: number;
  className?: string;
  style?: CSSProperties;
}) {
  const mark = Math.round(wordSize * MARK_RATIO * 100) / 100;
  const gap = Math.round(wordSize * GAP_RATIO * 100) / 100;

  return (
    <span
      className={`inline-flex items-center font-semibold tracking-[-0.02em] ${className}`}
      style={{ fontSize: `${wordSize}px`, gap: `${gap}px`, ...style }}
    >
      <LogoMark
        className=""
        style={{ width: `${mark}px`, height: `${mark}px` }}
      />
      SenseCollect
    </span>
  );
}
